/**
 * Computes the flags emoji string rendered on a Scheduler job tile.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS:
 * --------------------------------------------------------------------------
 *
 * Extracted from `schedule.context.js` projection loop (the function that
 * sets `newJob.flags`). Extraction allows adversarial regression tests to
 * pin the expedite/distant fallback behavior introduced for #159 so that
 * future edits to the emoji string cannot silently drop the fallback.
 *
 * --------------------------------------------------------------------------
 * THE BUG THIS HELPS PREVENT (#159):
 * --------------------------------------------------------------------------
 *
 * Expedite + Long-Distance flags follow a HYBRID Attributes/FileMaker
 * source-of-truth rule (see CLAUDE.md "Known Pitfalls"):
 *   • If a per-job `Attributes` record exists, the projection loop's
 *     override block syncs `document.expediteFlag → jobAttributes.expedite`.
 *   • If NO Attributes record exists yet (FAV-auto-pre-scheduled status-1.2
 *     jobs that no human has opened in JobsDetailModal), `jobAttributes`
 *     is undefined and the emoji condition silently evaluates falsy —
 *     leaving the tile without 🏃🏽‍♂️ even though FileMaker has
 *     expediteFlag=true.
 *
 * The fix is a nullish-coalescing fallback: read jobAttributes.expedite
 * first, then fall back to document.expediteFlag. Same pattern for distant.
 *
 * Tests that import this helper directly cannot drift from production.
 */

// ⚠️ TWO THINGS HERE ARE LOAD-BEARING AND MEASURED — do not "tidy" either.
//
// 1. `thanksgivingRush` is 🦃 (turkey), not 🎃 (pumpkin) — #514, John Sarappo /
//    SUP-9966. ⚠️ The pumpkin was NOT a mistake: both holiday emoji shipped
//    last year and worked fine, and some of the office staff deliberately
//    preferred the pumpkin (Halloween) over a turkey. They were outvoted for
//    2026 (Leland, 2026-09-10) — so this is a STAFF PREFERENCE CHANGE, not a
//    bug fix, and if the vote swings back the change is this one line.
//    NOTE: SUP-9966
//    also carries an SVG asset. The Scheduler's flag row is a concatenated
//    EMOJI STRING rendered into the tile — an SVG cannot go in it — so the
//    emoji is the in-idiom equivalent. If that SVG is meant to render inside
//    the Scheduler (rather than in FileMaker), that is a different and much
//    larger change: see #514.
//
// 2. The comparisons below are NOT uniform, and the difference is deliberate.
//    Measured in Mongo on BOTH envs, 2026-09-10 (#518):
//        thanksgivingRushFlag  int    0/1        -> strict `=== 1` is CORRECT
//        christmasRushFlag     int    0/1        -> strict `=== 1` is CORRECT
//        expressShipFlag       STRING ""/"1"     -> strict `=== 1` was BROKEN
//    So `expressShip` is coerced with parseFloat and the other two are not.
//    Widening the seasonal checks would delete a working, intentionally-pinned
//    guard (their `not.toContain` tests) for no reason.
const EMOJI = {
  thanksgivingRush: "🦃",
  christmasRush: "🎄",
  expressShip: "📦",
  furniture: "🪑",
  prescheduled: "📅",
  rush: "⚡️",
  expedite: "🏃🏽‍♂️",
  distant: "✈️",
  imsWarning: "🛑",
};

/**
 * @param {object} newJob - Projected job object (must have jobAttributes already overlaid).
 * @param {object} document - Raw ActiveQueue document (source for FileMaker top-level flags).
 * @returns {string} Concatenated emoji string for the tile's `flags` field.
 */
export function computeFlagsEmojiString(newJob, document) {
  const safeJob = newJob || {};
  const safeDoc = document || {};

  // Expedite + Distant: hybrid Attributes/FileMaker source-of-truth.
  // jobAttributes.expedite ?? document.expediteFlag — nullish-coalescing
  // ensures we fall back to the FileMaker top-level flag when no Attributes
  // record exists yet. See #159 and CLAUDE.md "Known Pitfalls".
  const expedite =
    safeJob.jobAttributes?.expedite ?? safeDoc.expediteFlag;
  const distant =
    safeJob.jobAttributes?.distant ?? safeDoc.distantFlag;

  return (
    `${safeJob.thanksgivingRush === 1 ? EMOJI.thanksgivingRush : ""}` +
    `${safeJob.christmasRush === 1 ? EMOJI.christmasRush : ""}` +
    `${parseFloat(safeJob.expressShip) === 1 ? EMOJI.expressShip : ""}` +
    `${safeJob.furniture ? EMOJI.furniture : ""}` +
    `${safeJob.prescheduled ? EMOJI.prescheduled : ""}` +
    `${safeJob.rush ? EMOJI.rush : ""}` +
    `${expedite ? EMOJI.expedite : ""}` +
    `${distant ? EMOJI.distant : ""}` +
    `${safeJob.imsWarning ? EMOJI.imsWarning : ""}`
  );
}

export const FLAGS_EMOJI = EMOJI;
